import { Component, defineAsyncComponent } from "vue";
import { addBreadcrumb } from "@sentry/vue";

import { useWpsClient, WPSClient } from "@/wps/client";

import { PDFLoader } from "./pdf/loader";

import { assertNever } from "../../../modules/shared-utils/assertions";
import { decodeBase64Unicode } from "../../../modules/shared-utils/base64";
import { speculativeLoad } from "../../../modules/shared-utils/speculative";

const contentTypes = ["epub", "pdf"] as const;
export type ContentType = (typeof contentTypes)[number];

// embedders may pass `?preload=<type>` to start loading before the session resolves
export function parseContentType(value: unknown): ContentType | null {
  return contentTypes.find((type) => type === value) ?? null;
}

function startLoad(
  type: ContentType,
  client: WPSClient,
  signal?: AbortSignal
): Promise<Component> {
  switch (type) {
    case "epub":
      return import("./NavigatorContainer.vue");
    case "pdf":
      return new PDFLoader(client, signal).get();
    default:
      assertNever(type);
  }
}

function typeFromPath(path: string): ContentType | null {
  // base64 encoded gsuri
  if (path.startsWith("Z3M6")) {
    path = decodeBase64Unicode(path);
  }

  if (!path.startsWith("gs:")) {
    return null;
  }

  if (path.endsWith(".epub")) {
    return "epub";
  } else if (path.endsWith(".pdf")) {
    return "pdf";
  }
  return null;
}

async function typeFromSession(client: WPSClient): Promise<ContentType> {
  const session = await client.session();
  const type = typeFromPath(session.id);
  if (type) return type;

  const manifest = await client.manifest();
  if (
    manifest.metadata?.otherMetadata?.conformsTo ===
    "https://readium.org/webpub-manifest/profiles/pdf"
  ) {
    return "pdf";
  } else {
    return "epub";
  }
}

function loadWithHint(
  client: WPSClient,
  hint: ContentType | null
): Promise<Component> {
  const known = typeFromPath(client.token.raw);
  if (known) return startLoad(known, client);

  const actual = typeFromSession(client);
  if (!hint) return actual.then((type) => startLoad(type, client));

  const start = (type: ContentType, signal: AbortSignal) =>
    startLoad(type, client, signal);
  return speculativeLoad(hint, actual, start, (outcome, error) => {
    if (outcome === "hit") return;
    addBreadcrumb({
      category: "navigator",
      level: "warning",
      message: `preload=${hint} ${outcome}`,
      data: error ? { error: String(error) } : undefined,
    });
  });
}

const clients = new WeakMap<WPSClient, Promise<Component>>();

export function loadFromWpsClient(
  client: WPSClient,
  hint: ContentType | null = null
): Promise<Component> {
  if (clients.has(client)) {
    return clients.get(client)!;
  }

  const promise = loadWithHint(client, hint);
  clients.set(client, promise);
  return promise;
}

export function useNavigatorComponent() {
  const wpsClient = useWpsClient();
  return defineAsyncComponent(() => loadFromWpsClient(wpsClient));
}
